// tslint:disable max-func-body-length
import { useMemo, useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { DefaultFilterValidation } from 'app/area/shared/default-filters/types-store';
import { SavedReportsValidation } from 'app/area/shared/saved-reports/types-store';
import { CompanyReportAdminTranslation } from '../../constants';
import { CompanyReportItemValidation, TextsValidation, MetadataValidation, SavedReportCompanyReport } from '../types-store';
import { CompanyReportSectionType } from './types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getCompanyReportRoute = (
  companyReportId: number,
  section?: CompanyReportSectionType
) => isDefinedIncludingZero(companyReportId)
    ? routeConstants.companyReportAdminItemFor(companyReportId, section)
    : routeConstants.companyReportAdminAddFor(section);

export const companyReportConfiguration = (
  companyReportId: number,
  validationErrors: CompanyReportItemValidation,
  savedReportsValidation: SavedReportsValidation<SavedReportCompanyReport>,
  filterValidation: { [domainId: number]: DefaultFilterValidation },
  textsValidation: TextsValidation,
  metadataValidation: MetadataValidation
): Configuration<CompanyReportSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement>();
  const visibilitySectionRef = useRef<HTMLDivElement>();
  const savedReportsSectionRef = useRef<HTMLDivElement>();
  const filtersSectionRef = useRef<HTMLDivElement>();
  const emailSettingsSectionRef = useRef<HTMLDivElement>();
  const textsSectionRef = useRef<HTMLDivElement>();
  const metadataSectionRef = useRef<HTMLDivElement>();
  const excelMappingsSectionRef = useRef<HTMLDivElement>();
  const translationsSectionRef = useRef<HTMLDivElement>();

  return useMemo(() => {

    const getSectionRoute = (section: CompanyReportSectionType) => getCompanyReportRoute(companyReportId, section);

    const renderLanguageOnTop = !isDefinedIncludingZero(companyReportId);
    const languageSection = {
      section: CompanyReportSectionType.Translations,
      visible: true,
      url: getSectionRoute(CompanyReportSectionType.Translations),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: CompanyReportAdminTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: isValid([validationErrors.language], ValidationLevel.error)
    };

    const savedReportValues = Object.values(savedReportsValidation ?? {});

    return [
      ...(renderLanguageOnTop ? [languageSection] : []),
      {
        section: CompanyReportSectionType.Details,
        visible: true,
        iconUrl: '/i/case-sections/default.svg',
        url: getSectionRoute(CompanyReportSectionType.Details),
        label: (translation: CompanyReportAdminTranslation) => translation.details,
        ref: detailsSectionRef,
        isValid: isValid([
          validationErrors.sequence, validationErrors.disabled,
          validationErrors.template, validationErrors.defaultTemplate
        ], ValidationLevel.error)
      },
      {
        section: CompanyReportSectionType.Visibility,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(CompanyReportSectionType.Visibility),
        label: (translation: CompanyReportAdminTranslation) => translation.visibility,
        ref: visibilitySectionRef,
        isValid: isValid([validationErrors.subset], ValidationLevel.error)
      },
      {
        section: CompanyReportSectionType.SavedReports,
        visible: true,
        iconUrl: '/i/case-sections/connected-cases.svg',
        url: getSectionRoute(CompanyReportSectionType.SavedReports),
        label: (translation: CompanyReportAdminTranslation) => translation.saved_reports,
        ref: savedReportsSectionRef,
        isValid: savedReportValues.every(savedReport => isValid(Object.values(savedReport), ValidationLevel.error)) &&
          isValid([validationErrors.usePredefDate, validationErrors.predefinedDate], ValidationLevel.error),
        hasWarnings: savedReportValues.some(savedReport => !isValid(Object.values(savedReport), ValidationLevel.warning))
      },
      {
        section: CompanyReportSectionType.Filters,
        visible: true,
        iconUrl: '/i/funnel.svg',
        url: getSectionRoute(CompanyReportSectionType.Filters),
        label: (translation: CompanyReportAdminTranslation) => translation.filters,
        ref: filtersSectionRef,
        isValid: Object.values(filterValidation ?? {}).every(filter => isValid(Object.values(filter), ValidationLevel.error))
      },
      {
        section: CompanyReportSectionType.EmailSettings,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(CompanyReportSectionType.EmailSettings),
        label: (translation: CompanyReportAdminTranslation) => translation.email_settings,
        ref: emailSettingsSectionRef,
        isValid: isValid([
          validationErrors.emailSetting,
          validationErrors.emailReport,
          validationErrors.textId, validationErrors.textSet, validationErrors.textDescription
        ], ValidationLevel.error)
      },
      {
        section: CompanyReportSectionType.Texts,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(CompanyReportSectionType.Texts),
        label: (translation: CompanyReportAdminTranslation) => translation.texts_title,
        ref: textsSectionRef,
        isValid: !Object.keys(textsValidation ?? {}).length && (validationErrors?.text?.length > 0
          ? !validationErrors.text.find(texts => !isValid([texts], ValidationLevel.error))
          : true)
      },
      {
        section: CompanyReportSectionType.Metadata,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(CompanyReportSectionType.Metadata),
        label: (translation: CompanyReportAdminTranslation) => translation.metadata_title,
        ref: metadataSectionRef,
        isValid: !validationErrors?.metadata?.find(metadata => !isValid([metadata], ValidationLevel.error))
      },
      {
        section: CompanyReportSectionType.ExcelMappings,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(CompanyReportSectionType.ExcelMappings),
        label: (translation: CompanyReportAdminTranslation) => translation.excel_mappings,
        ref: excelMappingsSectionRef,
        isValid: true
      },
      ...(!renderLanguageOnTop ? [languageSection] : [])
    ];
  }, [companyReportId, textsValidation, metadataValidation, validationErrors, savedReportsValidation, filterValidation,
    detailsSectionRef, visibilitySectionRef, emailSettingsSectionRef, translationsSectionRef]);
};
